import { container } from '@intigral/domain';
import { userAccessControl } from './userAccessControl';
import type { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { DI } from '@intigral/constant/config/DIContainer';
import cdnHandler from '../helpers/CDNHandler';
import playerService from '../helpers/bitmovinPlayer';
import { playerTypes } from '../features/player/constants';
import type { UIManager, UIVariant } from 'bitmovin-player-ui';
import type { PlayerAPI } from 'bitmovin-player';
import type { RefObject } from 'react';
import type { IContentDetailsResponse } from '@intigral/domain/lib/entity/interface/iMpxVodResponse';

/**
 * Represents an extracted Ad Period from the DASH Manifest.
 */
interface IAdDetails {
    id: string;
    durationSeconds: number;
    startAt: number;
    endAt: number;
    played: boolean;
    finished: boolean;
    position: number;
}

interface IAdData {
    id: string;
    startPosition: number;
    duration: number;
    ads: {
        adType: string;
        index: number;
        creativeId: string;
        adId: string;
        startPosition: number;
        skipPosition: number;
        duration: number;
        clickURL: string;
        nonLinearIframeResources: any[];
        nonLinearStaticResources: any[];
    }[];
    adCount: number;
}

/**
 * Represent a group of connected ads.
 */
interface IAdPod {
    startAt: number;
    endAt: number;
    totalDuration: number;
    played: boolean;
    finished: boolean;
    ads: IAdDetails[];
}

interface IPlayerSeekEvent {
    /** The type of the event, e.g., "seeked" */
    type: string;
    /** The timestamp when the seek event was triggered */
    timestamp: number;
    /** The current position of the playhead when the seek event was triggered */
    position: number;
    /** The target position to seek to */
    seekTarget: number;
    /** The issuer of the seek event */
    issuer: string;
}

interface IContentDetails {
    adEnabled: boolean;
    adTypes: string[];
    runtime: number;
}

interface IMarkerForSSAI {
    time: number;
    duration: number;
    title?: string;
    cssClasses: string[];
}

interface IPlayerTimeChangeEvent {
    type: string;
    timestamp: number;
    time: number;
}

const enum PeriodType {
    Content,
    Ad,
    Unknown,
}

const emptyEvent = (): void => {};

// SSAI manager for the Player.
class PlayerSSAIManager {
    public markers: IMarkerForSSAI[] = [];
    public readonly canRunSSAI: boolean = false;
    public readonly currentPlayingAd: IAdDetails | null = null;
    public readonly currentPlayingAdPod: IAdPod | null = null;
    public readonly currentPlayerTime: number = 0;
    /** Total duration of the content only (excluding all ad periods), in seconds. */
    public readonly contentDuration: number = 0;
    public readonly mpxDetails: IContentDetailsResponse | null = null;

    public onAdPodStart: (ad: IAdPod) => void = emptyEvent;
    public onAdPodEnded: (ad: IAdPod) => void = emptyEvent;
    public onAdPodInProgress: (ad: IAdPod) => void = emptyEvent;

    public onAdStart: (ad: IAdDetails) => void = emptyEvent;
    public onAdEnded: (ad: IAdDetails) => void = emptyEvent;
    public onAdInProgress: (ad: IAdDetails) => void = emptyEvent;
    public onAdPaused: (ad: IAdDetails) => void = emptyEvent;
    public onAdResumed: (ad: IAdDetails) => void = emptyEvent;
    public onAdExit: (ad: IAdDetails) => void = emptyEvent;

    public onReady: (adPods: IAdPod[], adList: IAdData[]) => void = emptyEvent;
    public onVisibilityChanged: (visible: boolean) => void = emptyEvent;
    public onBeforeDestroy: () => void = emptyEvent;
    public onDestroyed: () => void = emptyEvent;

    private extractedAds: IAdDetails[] | null = null;
    private adPods: IAdPod[] | null = null;
    private adList: IAdData[] | null = null;
    private hasAds: boolean = false;
    private adListProcessed: boolean = false;
    private uiManagerRef: RefObject<UIManager> | null = null;
    private uiVariants: Map<string, UIVariant> = new Map();
    private uiMarkers: Map<number, { marker: HTMLElement; style: string }> = new Map();

    private adsUIElementsInitalized: boolean = false;
    private mainUIElement: HTMLElement | null = null;
    private adsUIElement: HTMLElement | null = null;
    private videoElement: HTMLVideoElement | null = null;
    private adsSeekbar: HTMLElement | null = null;
    private currentAdNumberElement: HTMLElement | null = null;
    private totalAdsCountElement: HTMLElement | null = null;
    private currentAdsTimeElement: HTMLElement | null = null;
    private totalAdsTimeElement: HTMLElement | null = null;

    private seekingTo: number = -1;

    // Stored references for player method patching (content-only seekbar)
    private patchedPlayer: PlayerAPI | null = null;
    private originalGetDuration: (() => number) | null = null;
    private originalGetCurrentTime: (() => number) | null = null;
    private originalSeek: ((target: number, issuer?: string) => boolean) | null = null;

    private visibilityChangedHandler = () => this.onVisibilityChanged(!document.hidden);

    public init(playerType: string, uiManagerRef: RefObject<UIManager>) {
        (this.canRunSSAI as boolean) =
            playerType === playerTypes.Vod &&
            userAccessControl.userInfo?.segment === 'B2C' &&
            !userAccessControl.isPremiumUser;
        if (this.canRunSSAI) {
            this.uiManagerRef = uiManagerRef;
            document.addEventListener('visibilitychange', this.visibilityChangedHandler);
        }
    }

    public async addSSAIParamsToCDNHandlerURL(parsedUrl: URL) {
        const contentDetails = await this.getContentAdDetailsFromMPX();
        if (contentDetails?.adEnabled !== true) return;

        parsedUrl.searchParams.delete('ssai'); // Remove 'ssai' param, it could cause conflict.

        cdnHandler.avodConfig.vodAdsUrlParams.forEach((param) => {
            parsedUrl.searchParams.set(param.key, param.value);
        });

        if (contentDetails.adTypes?.includes('preroll')) parsedUrl.searchParams.set('vmapg_pre', 'true');

        if (contentDetails.adTypes?.includes('midroll')) {
            const runtime = contentDetails.runtime;
            const rules = cdnHandler.avodConfig.midRoll.rules;
            for (const rule of rules) {
                const min = runtime >= rule.minDuration;
                const max = Number.isInteger(rule.maxDuration) ? runtime <= rule.maxDuration : true;
                if (min && max) {
                    const adSlots = rule.adSlots.map((slot) => slot.positionPct + '%').join();
                    parsedUrl.searchParams.set('vmapg_mids', adSlots);
                    break;
                }
            }
        }
    }

    public handlePlayerSeek(event: IPlayerSeekEvent) {
        if (!this.hasAds) return;
        if (event.issuer === 'ui' || Math.abs(~~(event.position - event.seekTarget)) === 10) {
            // UI seekTarget is in content-time (from the patched seekbar); convert to raw-time
            // so it aligns with adPod start/end boundaries which are in raw timeline time.
            const targetTime = event.issuer === 'ui' ? this.getRawTime(event.seekTarget) : event.seekTarget;
            const adPods = this.adPods;
            let seekTo = -1;
            for (let i = adPods.length - 1; i >= 0; i--) {
                const adPod = adPods[i];
                const isSeekedToAd = this.isTimeWithinAdPodRange(targetTime, adPod);
                if (isSeekedToAd || targetTime >= adPod.endAt) {
                    if (adPod.finished) break;
                    if (!isSeekedToAd)
                        window.sessionStorage.setItem('SSAI-Player-Seek-Time', Math.round(targetTime).toString());
                    seekTo = adPod.startAt;
                    break;
                }
            }
            if (seekTo >= 0) this.seekPlayerTo(seekTo);
        }
    }
    public handleTimeChange(currentTime: number) {
        if (!this.hasAds) return;
        (this.currentPlayerTime as number) = currentTime;
        this.updatePlayingAds(currentTime);
    }
    public handlePlayerPaused(): void {
        const currentAd = this.currentPlayingAd;
        if (currentAd) this.onAdPaused(currentAd);
    }
    public handlePlayerResumed(): void {
        const currentAd = this.currentPlayingAd;
        if (currentAd) this.onAdResumed(currentAd);
    }
    public handlePlayerDestroyed(): void {
        const currentAd = this.currentPlayingAd;
        if (currentAd) this.onAdExit(currentAd);
        this.clear();
    }

    public processAdList(adList: IAdData[]) {
        if (!Array.isArray(adList) || adList.length < 1) return;

        const extractedAds: IAdDetails[] = [];
        const mergedAds: IAdPod[] = [];
        adList.forEach((adData) => {
            let ads: IAdDetails[] = [];
            let position = 0;
            adData.ads.forEach((ad) => {
                const singleAd = {
                    id: ad.adId,
                    durationSeconds: ad.duration / 1000,
                    startAt: ad.startPosition / 1000,
                    endAt: (ad.startPosition + ad.duration) / 1000,
                    played: false,
                    finished: false,
                    position: position++,
                };
                extractedAds.push(singleAd);
                ads.push(singleAd);
            });
            mergedAds.push({
                startAt: adData.startPosition / 1000,
                endAt: (adData.startPosition + adData.duration) / 1000,
                totalDuration: adData.duration / 1000,
                played: false,
                finished: false,
                ads: ads,
            });
        });
        this.adList = adList;
        this.extractedAds = extractedAds;
        this.adPods = mergedAds;
        this.adListProcessed = true;
        this.hasAds = true;
        this.generatePlayerMarkers();
    }

    public extractAdPeriods(manifestXml: string): void {
        if (!this.canRunSSAI || this.adListProcessed) return;

        const ads: IAdDetails[] = [];
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(manifestXml, 'application/xml');
        const periods = xmlDoc.getElementsByTagName('Period');

        let cumulativeTimeSec = 0;

        for (let i = 0; i < periods.length; i++) {
            const period = periods[i];
            const id = period.getAttribute('id') || `unknown_${i}`;
            const durationStr = period.getAttribute('duration') || '';

            const durationSec = this.parseIsoDurationToSeconds(durationStr);

            const isAdPeriod = this.getPeriodType(period) === PeriodType.Ad;

            if (durationSec > 0 && isAdPeriod) {
                ads.push({
                    id: id,
                    durationSeconds: durationSec,
                    startAt: cumulativeTimeSec,
                    endAt: cumulativeTimeSec + durationSec,
                    played: false,
                    finished: false,
                    position: 0,
                });
            }

            // Add the current period's duration to the running timeline
            cumulativeTimeSec += durationSec;
        }
        this.extractedAds = ads;
        this.hasAds = ads.length > 0;
        // Calculate content-only duration (total timeline minus all ad durations)
        const totalAdDuration = ads.reduce((sum, ad) => sum + ad.durationSeconds, 0);
        (this.contentDuration as number) = Math.max(0, cumulativeTimeSec - totalAdDuration);
        if (this.hasAds) {
            this.mergeConnectedAds(1);
            this.generatePlayerMarkers();
            this.onReady(this.adPods ?? [], this.adList ?? []);
        }
    }

    public clear(): void {
        this.onBeforeDestroy();
        this.unpatchPlayerDuration();
        this.onBeforeDestroy = emptyEvent;
        this.extractedAds = null;
        this.adPods = null;
        this.adList = null;
        this.markers = [];
        (this.canRunSSAI as boolean) = false;
        this.onAdStart = emptyEvent;
        this.onAdEnded = emptyEvent;
        this.onAdInProgress = emptyEvent;
        this.onAdPaused = emptyEvent;
        this.onAdResumed = emptyEvent;
        this.onAdExit = emptyEvent;
        this.onAdPodStart = emptyEvent;
        this.onAdPodEnded = emptyEvent;
        this.onAdPodInProgress = emptyEvent;
        this.onVisibilityChanged = emptyEvent;
        this.onReady = emptyEvent;
        (this.currentPlayingAd as IAdDetails) = null;
        (this.currentPlayingAdPod as IAdPod) = null;
        (this.currentPlayerTime as number) = 0;
        (this.contentDuration as number) = 0;
        (this.mpxDetails as IContentDetailsResponse) = null;
        this.hasAds = false;
        this.adListProcessed = false;
        this.uiManagerRef = null;
        this.uiVariants.clear();
        this.uiMarkers.clear();
        this.adsUIElementsInitalized = false;
        this.mainUIElement = null;
        this.adsUIElement = null;
        this.adsSeekbar = null;
        this.currentAdNumberElement = null;
        this.totalAdsCountElement = null;
        this.currentAdsTimeElement = null;
        this.totalAdsTimeElement = null;
        this.seekingTo = -1;
        window.sessionStorage.removeItem('SSAI-Player-Seek-Time');
        document.removeEventListener('visibilitychange', this.visibilityChangedHandler);
        this.onDestroyed();
        this.onDestroyed = emptyEvent;
    }

    public saveCurrentUIState() {
        if (!this.UIManager) return;

        const savedVariant = this.uiVariants.get('saved-ui');
        if (savedVariant) return;

        this.mainUIElement = this.getActiveUIElement();
        if (!this.mainUIElement) {
            console.error('[SSAI] Could not find the active UI element!');
            return;
        }

        // Save markers state:
        this.mainUIElement
            .querySelectorAll(`div.bmpui-seekbar-markers .bmpui-ad-marker[data-marker-time]`)
            .forEach((marker: HTMLElement, index: number) => {
                marker.style.removeProperty('transition-duration');
                this.uiMarkers.set(index, { marker, style: marker.getAttribute('style') });
            });

        const activeUIContainer = this.UIManager.activeUi.getUI();
        const activeVariant = this.UIManager.getUiVariants().find((v) => v.ui === activeUIContainer);
        if (activeVariant) {
            this.uiVariants.set('saved-ui', activeVariant);
        }
    }
    public restoreSavedUIState() {
        if (!this.UIManager) return;
        const savedVariant = this.uiVariants.get('saved-ui');
        if (savedVariant) {
            this.UIManager.switchToUiVariant(savedVariant);
        }
        this.setUIElementVisiblity(this.adsUIElement, false);
        this.setUIElementVisiblity(this.mainUIElement, true);
        this.uiMarkers.forEach((markerData) => {
            markerData.marker.setAttribute('style', markerData.style);
        });
        this.restoreSavedTime();
    }
    public switchToAdsUI() {
        if (!this.UIManager) return;
        const adsUIVariantID = 'ads-ui';
        const savedVariant = this.uiVariants.get(adsUIVariantID);
        if (savedVariant) {
            this.UIManager.switchToUiVariant(savedVariant);
        } else {
            const targetVariant = this.UIManager.getUiVariants().find((v) => v.ui.getConfig().id === adsUIVariantID);
            if (targetVariant) {
                this.UIManager.switchToUiVariant(targetVariant);
                this.uiVariants.set(adsUIVariantID, targetVariant);
                this.initAdsUIElements();
            }
        }
    }
    public restoreSavedTime() {
        const savedTime = window.sessionStorage.getItem('SSAI-Player-Seek-Time');
        if (savedTime) {
            const savedTimeInSeonds = parseInt(savedTime);
            if (Number.isFinite(savedTimeInSeonds)) {
                this.seekPlayerTo(savedTimeInSeonds);
            }
            window.sessionStorage.removeItem('SSAI-Player-Seek-Time');
        }
    }

    public setVideoElement(videoElement: HTMLVideoElement) {
        this.videoElement = videoElement;
        videoElement.onloadedmetadata = () => {
            if (!this.canRunSSAI) return;
            videoElement.ontimeupdate = () => {
                if (videoElement.currentTime > 0) {
                    this.skipFinishedAdPod(videoElement.currentTime);
                    if (!videoElement.seeking) {
                        this.seekingTo = -1;
                        this.handleTimeChange(videoElement.currentTime);
                    }
                }
            };
            videoElement.onseeking = (event: Event) => {
                this.skipFinishedAdPod(videoElement.currentTime);
            };
            this.onReady(this.adPods ?? [], this.adList ?? []);
        };
    }

    public get hasActiveAds() {
        return this.canRunSSAI && this.hasAds;
    }

    /**
     * Patches the player's getDuration, getCurrentTime, and seek methods
     * so the seekbar and time label operate on a content-only time scale
     * (ad durations are excluded from the timeline).
     * Call this after the player is ready and ads have been extracted.
     */
    public patchPlayerDuration(player: PlayerAPI): void {
        if (!this.hasActiveAds || this.patchedPlayer) return;

        this.patchedPlayer = player;
        const self = this;

        // Save originals
        this.originalGetDuration = player.getDuration.bind(player);
        this.originalGetCurrentTime = player.getCurrentTime.bind(player);
        this.originalSeek = player.seek?.bind(player) ?? null;

        // Override getDuration to return content-only duration
        player.getDuration = () => self.contentDuration;

        // Override getCurrentTime to return content-adjusted time
        player.getCurrentTime = () => {
            const rawTime = self.originalGetCurrentTime!();
            return self.getContentTime(rawTime);
        };

        // Override seek to convert content-time targets back to raw-time
        if (player.seek && self.originalSeek) {
            player.seek = (target: number, issuer?: string): boolean => {
                const rawTarget = self.getRawTime(target);
                return self.originalSeek!(rawTarget, issuer);
            };
        }

        // Regenerate markers now that the player duration is patched to content-only time.
        // Markers were previously passed to Bitmovin with content-time values, but the UI
        // positioned them as percentages of the raw (unpatched) duration. Regenerating
        // and updating the DOM fixes their positions on the content-time seekbar.
        this.generatePlayerMarkers();
        requestAnimationFrame(() => {
            this.updateMarkerPositionsInDOM();
        });
    }

    /**
     * Restores the player's original getDuration, getCurrentTime, and seek methods.
     * Called during cleanup.
     */
    public unpatchPlayerDuration(): void {
        const player = this.patchedPlayer;
        if (!player) return;

        if (this.originalGetDuration) {
            player.getDuration = this.originalGetDuration;
            this.originalGetDuration = null;
        }
        if (this.originalGetCurrentTime) {
            player.getCurrentTime = this.originalGetCurrentTime;
            this.originalGetCurrentTime = null;
        }
        if (this.originalSeek && player.seek) {
            player.seek = this.originalSeek;
            this.originalSeek = null;
        }

        this.patchedPlayer = null;
    }

    private mergeConnectedAds(maxGapSeconds: number) {
        if (!this.hasAds) return;

        const ads = this.extractedAds;
        const mergedAds: IAdPod[] = [];

        let currentAds: IAdDetails[] = [ads[0]];
        let startAt = ads[0].startAt;
        let endAt = ads[0].startAt + ads[0].durationSeconds;

        for (let i = 1; i < ads.length; i++) {
            const ad = ads[i];

            // Connected if it starts where the previous one ends (±buffer seconds)
            if (Math.abs(endAt - ad.startAt) <= maxGapSeconds) {
                currentAds.push(ad);
                endAt = ad.startAt + ad.durationSeconds;
            } else {
                mergedAds.push({
                    startAt,
                    endAt,
                    totalDuration: endAt - startAt,
                    played: currentAds.every((a) => a.played),
                    finished: currentAds.every((a) => a.finished),
                    ads: currentAds,
                });

                currentAds = [ad];
                startAt = ad.startAt;
                endAt = ad.startAt + ad.durationSeconds;
            }
        }

        // Push the last group
        mergedAds.push({
            startAt,
            endAt,
            totalDuration: endAt - startAt,
            played: currentAds.every((a) => a.played),
            finished: currentAds.every((a) => a.finished),
            ads: currentAds,
        });

        for (const pod of mergedAds) {
            pod.ads.forEach((ad, index) => {
                ad.position = index + 1;
            });
        }

        this.adPods = mergedAds;
    }

    private updatePlayingAds(currentTime: number) {
        const currentAdPod = this.currentPlayingAdPod;
        if (currentAdPod) {
            const currentAd = this.currentPlayingAd;
            if (this.isTimeWithinAdPodRange(currentTime, currentAdPod)) {
                this.onAdPodInProgress(currentAdPod);
                this.updateUIElementsWhenAdPodInProgress(currentTime);
                if (currentAd) {
                    if (this.isTimeWithinAdRange(currentTime, currentAd)) {
                        this.onAdInProgress(currentAd);
                    } else if (currentTime >= currentAd.endAt) {
                        this.endPlayingAd(currentTime, currentAd);
                        this.startMatchingAd(currentTime, currentAdPod.ads);
                    }
                } else {
                    this.startMatchingAd(currentTime, currentAdPod.ads);
                }
            } else if (currentTime >= currentAdPod.endAt) {
                if (currentAd) this.endPlayingAd(currentTime, currentAd);
                currentAdPod.finished = true;
                this.onAdPodEnded(currentAdPod);
                (this.currentPlayingAdPod as IAdPod) = null;
            }
        } else {
            const adPod = this.adPods.find((adPod) => this.isTimeWithinAdPodRange(currentTime, adPod));
            if (adPod) {
                if (adPod.finished) {
                    this.seekPlayerTo(adPod.endAt);
                } else {
                    if (currentTime - adPod.startAt > 3) {
                        this.seekPlayerTo(adPod.startAt);
                    } else {
                        adPod.played = true;
                        (this.currentPlayingAdPod as IAdPod) = adPod;
                        this.onAdPodStart(adPod);
                        this.updateUIElementsWhenAdPodStart(currentTime);
                        this.startMatchingAd(currentTime, adPod.ads);
                    }
                }
            }
        }
    }
    private startMatchingAd(currentTime: number, ads: IAdDetails[]) {
        const newAd = ads.find((ad) => this.isTimeWithinAdRange(currentTime, ad));
        if (newAd) {
            newAd.played = true;
            (this.currentPlayingAd as IAdDetails) = newAd;
            this.onAdStart(newAd);
            this.updateUIElementsWhenAdStart();
        }
    }
    private endPlayingAd(currentTime: number, currentAd: IAdDetails) {
        currentAd.finished = true;
        this.onAdEnded(currentAd);
        (this.currentPlayingAd as IAdDetails) = null;
    }

    private generatePlayerMarkers() {
        this.markers =
            this.adPods
                .filter((adPod) => !adPod.finished)
                .map((ad, index) => {
                    // Convert raw timeline time to content-only time so markers
                    // align correctly with the patched content-only seekbar scale.
                    const contentTime = this.getContentTime(ad.startAt);
                    return {
                        time: contentTime,
                        duration: 0, // Zero-duration markers render as dots via CSS
                        title: '',
                        cssClasses: ['ad-marker'],
                    };
                })
                // Exclude markers at time 0 (preroll) — a dot at the seekbar start is invisible/useless
                .filter((marker) => marker.time > 0) || [];
    }

    /**
     * Updates the DOM marker elements' positions to match the newly calculated
     * content-time marker values. Bitmovin positions markers as percentages of
     * player.getDuration() at initialization time — if the duration was later
     * patched, the cached CSS `left` percentages are stale and need correction.
     */
    private updateMarkerPositionsInDOM(): void {
        if (!this.contentDuration) return;

        const markerElements = document.querySelectorAll<HTMLElement>(
            '.bmpui-seekbar-markers .bmpui-ad-marker[data-marker-time]'
        );

        if (markerElements.length === 0) return;

        const seekbar = document.querySelector('.bmpui-seekbar') as HTMLElement;
        const seekbarWidth = seekbar ? seekbar.clientWidth : 0;

        this.markers.forEach((marker, index) => {
            const el = markerElements[index];
            if (el) {
                const percent = (marker.time / this.contentDuration) * 100;
                const pixelPos = seekbarWidth ? (percent / 100) * seekbarWidth : 0;
                el.setAttribute('data-marker-time', marker.time.toString());
                el.style.transform = `translateX(${pixelPos}px)`;
            }
        });
    }

    /**
     * Returns the playback time adjusted to exclude already-played ad durations.
     * Use this to display content-only time in the player UI.
     * @param currentTime - The player's raw currentTime (includes ad periods).
     * @returns The content-only playback time in seconds.
     */
    public getContentTime(currentTime: number): number {
        if (!this.hasAds || !this.adPods) return currentTime;

        let elapsedAdTime = 0;
        for (const pod of this.adPods) {
            if (currentTime >= pod.endAt) {
                // This entire ad pod has already passed
                elapsedAdTime += pod.totalDuration;
            } else if (currentTime > pod.startAt) {
                // Currently inside this ad pod — only count the part that has elapsed
                elapsedAdTime += currentTime - pod.startAt;
                break;
            } else {
                // Haven't reached this pod yet
                break;
            }
        }
        return Math.max(0, Math.abs(currentTime - elapsedAdTime));
    }

    /**
     * Converts a content-only time back to the corresponding raw timeline time
     * (including ad periods). Used to map seek targets when the player duration
     * is patched to show content-only time.
     * @param contentTime - The content-only time to convert.
     * @returns The corresponding raw timeline time in seconds.
     */
    public getRawTime(contentTime: number): number {
        if (!this.hasAds || !this.adPods) return contentTime;

        let rawTime = 0;
        let contentTimeAccum = 0;
        let prevPodEnd = 0;

        for (const pod of this.adPods) {
            const contentSegmentDuration = pod.startAt - prevPodEnd;

            if (contentTime <= contentTimeAccum + contentSegmentDuration) {
                // Target falls within this content segment (before the ad pod)
                return prevPodEnd + (contentTime - contentTimeAccum);
            }

            contentTimeAccum += contentSegmentDuration;
            prevPodEnd = pod.endAt;
        }

        // Target is after the last ad pod
        return prevPodEnd + (contentTime - contentTimeAccum);
    }

    public get youboraAdType() {
        return [this.adPods[0].startAt === 0 ? 'preroll' : 'midroll'];
    }

    public get youboraAdDetails() {
        return this.hasAds
            ? {
                  adDuration: this.adPods[0].totalDuration,
                  adPosition: this.adPods[0].startAt === 0 ? 'preroll' : 'midroll',
              }
            : null;
    }

    public get youboraAdBreakData() {
        return (
            this.adList ||
            this.adPods.map((pod) => ({
                ads: pod.ads.map((ad) => ({
                    adId: ad.id,
                    adType: 'linear',
                    clickURL: '',
                    creativeId: '',
                    duration: Math.floor(ad.durationSeconds * 1000),
                    index: ad.position,
                    skipPosition: 0,
                    startPosition: Math.floor(ad.startAt * 1000),
                })),
                duration: Math.floor(pod.totalDuration * 1000),
                id: pod.ads.map((ad) => ad.id).join(':'),
                startPosition: Math.floor(pod.startAt * 1000),
            }))
        );
    }

    private async getContentAdDetailsFromMPX(): Promise<IContentDetails | null> {
        const parsedURL = new URL(window.location.href);
        const encodedId = parsedURL.searchParams.get('id');
        if (!encodedId) return null;
        const contentId = window.atob(encodedId);
        const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
        try {
            const response = await mpxService.getContentDetails(contentId);
            (this.mpxDetails as IContentDetailsResponse) = response;
            if (response?.entries?.length !== 1) return null;
            const contentData = response.entries[0];
            return {
                adEnabled: contentData.pl1$adEnabled ?? false,
                adTypes: contentData.pl1$adType ?? [],
                runtime: contentData.runtime ?? 0,
            };
        } catch (e) {
            console.error('[SSAI] Could not retrieve ad details from MPX', e);
        }
        return null;
    }

    private parseIsoDurationToSeconds(duration: string): number {
        // Regex to extract Hours, Minutes, and Seconds from the PT... formatting
        const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?/;
        const matches = duration.match(regex);

        if (!matches) return 0;

        const hours = matches[1] ? parseFloat(matches[1]) : 0;
        const minutes = matches[2] ? parseFloat(matches[2]) : 0;
        const seconds = matches[3] ? parseFloat(matches[3]) : 0;

        return hours * 3600 + minutes * 60 + seconds;
    }

    private formatTime(milliseconds: number): string {
        const totalSeconds = (milliseconds / 1000) | 0;
        const minutes = (totalSeconds / 60) | 0;
        const seconds = totalSeconds - minutes * 60;
        return (minutes < 10 ? '0' : '') + minutes + ':' + ((seconds < 10 ? '0' : '') + seconds);
    }

    private isTimeWithinAdRange(currentTime: number, ad: IAdDetails): boolean {
        return currentTime >= ad.startAt && currentTime < ad.endAt;
    }

    private isTimeWithinAdPodRange(currentTime: number, ad: IAdPod): boolean {
        return currentTime >= ad.startAt && currentTime < ad.endAt;
    }

    private skipFinishedAdPod(time: number): boolean {
        if (!this.adPods || this.seekingTo !== -1) return false;
        const finishedAdPod = this.getFinishedAdPodInTime(time);
        if (finishedAdPod) {
            this.seekPlayerTo(finishedAdPod.endAt);
            return true;
        }
        return false;
    }
    private getFinishedAdPodInTime(time: number): IAdPod | null {
        if (!this.adPods) return null;
        for (const adPod of this.adPods) {
            if (this.isTimeWithinAdPodRange(time, adPod) && adPod.finished) {
                return adPod;
            }
        }
        return null;
    }

    private seekPlayerTo(timeInSeonds: number, issuer: 'ui' | 'api' = 'api') {
        if (timeInSeonds === this.seekingTo) {
            return;
        }
        this.seekingTo = timeInSeonds;
        // Use original seek to bypass content-time patching — internal seeks always use raw timeline times
        if (this.originalSeek) {
            this.originalSeek(timeInSeonds, issuer);
        } else {
            playerService?.player?.seek?.(timeInSeonds, issuer);
        }
    }

    private initAdsUIElements() {
        if (this.adsUIElementsInitalized) return;

        const adsContainer = this.getActiveUIElement();
        if (!adsContainer) {
            return;
        }

        this.adsUIElementsInitalized = true;
        this.adsUIElement = adsContainer;
        this.adsSeekbar = adsContainer.querySelector('#custom-ad-seekbar-progress');
        this.currentAdNumberElement = adsContainer.querySelector('#currentAdNumber');
        this.totalAdsCountElement = adsContainer.querySelector('#totalAdsCount');
        this.currentAdsTimeElement = adsContainer.querySelector('#currentAdsTime');
        this.totalAdsTimeElement = adsContainer.querySelector('#totalAdsTime');

        const noAdCTA = this.adsUIElement.querySelector('button.ui-no-ad-cta-button.bmpui-hidden');
        if (noAdCTA) noAdCTA.classList.remove('bmpui-hidden');
    }

    private updateUIElementsWhenAdPodStart(currentTime: number) {
        if (!this.adsUIElementsInitalized) return;
        const adPod = this.currentPlayingAdPod;
        this.adsSeekbar.style.width = '0%';
        this.currentAdNumberElement.textContent = '1';
        this.totalAdsCountElement.textContent = adPod.ads.length.toString();
        this.currentAdsTimeElement.textContent = this.formatTime((adPod.endAt - currentTime) * 1000);
        this.totalAdsTimeElement.textContent = this.formatTime(this.currentPlayingAdPod.totalDuration * 1000);
    }

    private updateUIElementsWhenAdPodInProgress(currentTime: number) {
        if (!this.adsUIElementsInitalized) return;
        const adPod = this.currentPlayingAdPod;
        const elapsed = currentTime - adPod.startAt;
        const seekbarPercent = Math.round((elapsed / adPod.totalDuration) * 100);
        this.adsSeekbar.style.width = (seekbarPercent < 0 ? 0 : seekbarPercent > 100 ? 100 : seekbarPercent) + '%';
        this.currentAdsTimeElement.textContent = this.formatTime((adPod.endAt - currentTime) * 1000);
    }

    private updateUIElementsWhenAdStart() {
        if (!this.adsUIElementsInitalized) return;
        this.currentAdNumberElement.textContent = this.currentPlayingAd.position.toString();
    }

    private getPeriodType(period: Element): PeriodType {
        if (period.querySelector('ContentProtection') || period.querySelector('AdaptationSet[contentType="image"]')) {
            return PeriodType.Content;
        }

        const baseUrl = period.querySelector('BaseURL')?.textContent?.trim();

        if (!baseUrl) return PeriodType.Unknown;
        if (baseUrl === 'dash/') return PeriodType.Content;
        return PeriodType.Ad;
    }

    private getActiveUIElement() {
        return this.UIManager?.activeUi ? this.UIManager.activeUi.getUI().getDomElement().get()[0] : null;
    }

    private get UIManager(): UIManager | null {
        const uiManager = this.uiManagerRef?.current || null;
        if (!uiManager) console.error(`[SSAI] No UI Manager`);
        return uiManager;
    }

    private setUIElementVisiblity(UIElement: HTMLElement, visible: boolean) {
        if (!UIElement) return;
        const UIClasses = UIElement.classList;
        if (visible) {
            UIClasses.add('bmpui-controls-shown');
            UIClasses.remove('bmpui-hidden', 'bmpui-controls-hidden');
        } else {
            UIClasses.add('bmpui-hidden');
            UIClasses.remove('bmpui-controls-shown');
        }
    }
}

export const playerSSAIManager = new PlayerSSAIManager();
